/*
 * Swiftype search
 * Shared by the "Swift Search" section (sections/swift-search.liquid) and the search drawer
 * (sections/search-drawer.liquid). Each `.st-search` widget on the page is
 * initialised independently, so multiple instances can coexist without the
 * global-scope / duplicate-id clashes the old inline script suffered from.
 */
(function () {
  const PER_PAGE = 10;
  // Customer centre help URLs differ per store (AU customer-centre-article, UK
  // customer-centre-articles, US customer-center-articles), so match by pattern.
  const HELP_URL = /\/pages\/customer-cent(?:re|er)-(?:article|hub)/;
  const HELP_URL_TERMS = ['customer-centre-article', 'customer-centre-hub', 'customer-center-article', 'customer-center-hub'];
  const SUGGEST_ALLOWED = ['/products/', '/pages/size-chart', '/collections/'];

  function escapeHtml(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Only allow http(s) and site-relative URLs to reach an href/src attribute,
  // so a crafted result can't inject javascript: (or similar) schemes.
  function safeUrl(url) {
    const value = String(url == null ? '' : url).trim();
    if (/^https?:\/\//i.test(value) || value.startsWith('/')) return value;
    return '#';
  }

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function stripHtml(html) {
    return html.replace(/<[^>]*>/g, '');
  }

  function normalizeDomain(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/.*$/, '');
  }

  // Exact hostname match (www. ignored) so "merrypeople.com" doesn't also
  // match "uk.merrypeople.com".
  function matchesDomain(url, domain) {
    if (!domain) return true;
    try {
      return new URL(url).hostname.toLowerCase().replace(/^www\./, '') === domain;
    } catch (e) {
      return false;
    }
  }

  // Each widget root renders the domain filter (theme setting, or the store's
  // primary domain) via snippets/swiftype-domain-filter.liquid. Fall back to
  // any other widget on the page in case a root was rendered without it.
  function getDomainFilter(root) {
    const source = root.hasAttribute('data-domain-filter')
      ? root
      : document.querySelector('.st-search[data-domain-filter]');
    return normalizeDomain(source ? source.dataset.domainFilter : '');
  }

  const SNIPPET_LENGTH = 110;
  const SNIPPET_LEAD = 30;

  function decodeEntities(text) {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  }

  function toPlainText(html) {
    return decodeEntities(stripHtml(html)).replace(/\s+/g, ' ').trim();
  }

  function queryTerms(query) {
    return String(query == null ? '' : query)
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  // Whole words starting with a query term, with a plural "s" dropped so
  // "returns" also finds "return" and "returned".
  function termRegex(terms) {
    const stems = terms.map((term) => escapeRegExp(/[^s]s$/i.test(term) && term.length > 3 ? term.slice(0, -1) : term));
    return new RegExp(`\\b((?:${stems.join('|')})\\w*)`, 'gi');
  }

  // Header and footer text every crawled page shares (announcement bar, nav,
  // footer links) is the common prefix and suffix of the result bodies. Only
  // trusted across several results, so shared page content isn't mistaken for
  // it. Trimmed to whole words.
  function getPageChrome(items) {
    const bodies = items.map((item) => toPlainText(item.body || '')).filter(Boolean);
    if (bodies.length < 3) return { prefix: '', suffix: '' };

    const first = bodies[0];
    const shortest = Math.min(...bodies.map((b) => b.length));
    let prefix = 0;
    while (prefix < shortest && bodies.every((b) => b[prefix] === first[prefix])) prefix++;
    let suffix = 0;
    while (
      suffix < shortest - prefix &&
      bodies.every((b) => b[b.length - 1 - suffix] === first[first.length - 1 - suffix])
    ) {
      suffix++;
    }

    const prefixText = first.slice(0, prefix);
    const suffixText = first.slice(first.length - suffix);
    return {
      prefix: prefixText.slice(0, prefixText.lastIndexOf(' ') + 1),
      suffix: suffixText.slice(suffixText.indexOf(' ') + 1 || suffixText.length),
    };
  }

  // Query matches in running prose come first. Matches in navigation lists,
  // headings and product cards (price and rating lines) read as noise out of
  // context: those are mostly capitalised words, so a match whose neighbourhood
  // is mostly capitalised is only used when there is nothing better.
  function findContentMatch(text, terms) {
    if (!terms.length) return { index: -1, prose: false };
    const regex = termRegex(terms);
    let fallback = -1;
    let match;
    while ((match = regex.exec(text))) {
      const around = text.slice(Math.max(0, match.index - 60), match.index + 60);
      if (/(?:Sale|Regular) price/i.test(around)) continue;
      if (isProse(around)) return { index: match.index, prose: true };
      if (fallback < 0) fallback = match.index;
    }
    return { index: fallback, prose: false };
  }

  function isProse(text) {
    const words = text.match(/[A-Za-z][A-Za-z']*/g) || [];
    const lower = words.filter((word) => word[0] === word[0].toLowerCase()).length;
    return words.length > 0 && lower / words.length >= 0.4;
  }

  // Start at the sentence holding the match when it's close, otherwise a few
  // words before it, so the match stays inside the two clamped lines.
  function excerptAround(text, index) {
    if (index <= SNIPPET_LEAD) return truncateWords(text, SNIPPET_LENGTH);

    const sentence = Math.max(
      text.lastIndexOf('. ', index),
      text.lastIndexOf('? ', index),
      text.lastIndexOf('! ', index)
    );
    if (sentence >= 0 && index - (sentence + 2) <= SNIPPET_LEAD) {
      return truncateWords(text.slice(sentence + 2), SNIPPET_LENGTH);
    }

    let start = text.indexOf(' ', index - SNIPPET_LEAD) + 1;
    if (start <= 0 || start > index) start = index;
    return '…' + truncateWords(text.slice(start), SNIPPET_LENGTH);
  }

  // The passage around the first query match in the page's prose, then the
  // page's description (the swiftype description meta tag in theme.liquid),
  // then any other match, then the start of its content.
  function getSnippet(item, terms, chrome) {
    let body = toPlainText(item.body || '');
    if (chrome.prefix && body.startsWith(chrome.prefix)) body = body.slice(chrome.prefix.length);
    if (chrome.suffix && body.endsWith(chrome.suffix)) body = body.slice(0, -chrome.suffix.length);

    const match = findContentMatch(body, terms);
    if (match.prose) return excerptAround(body, match.index);

    const description = toPlainText(item.description || '');
    if (description) return truncateWords(description, SNIPPET_LENGTH);
    if (match.index >= 0) return excerptAround(body, match.index);
    return truncateWords(body, SNIPPET_LENGTH);
  }

  function truncateWords(text, length) {
    if (text.length <= length) return text;
    const cut = text.slice(0, length + 1);
    const end = cut.lastIndexOf(' ');
    return (end > 0 ? cut.slice(0, end) : text.slice(0, length)).replace(/[\s,;:.\-]+$/, '') + '…';
  }

  function getPrice(body) {
    const match = body.match(/Sale price\$?([\d,]+\.?\d*)\s*([A-Z]{3})/);
    return match ? `$${match[1]} ${match[2]}` : null;
  }

  function categorizeResults(results) {
    const cats = {};
    results.forEach((item) => {
      let category;
      if (HELP_URL.test(item.url)) {
        category = 'Help';
      } else if (item.url.includes('/blogs/')) {
        category = 'Blog Posts';
      } else if (item.url.includes('/pages/size')) {
        category = 'Size Charts';
      } else if (item.url.includes('/collections/')) {
        category = 'Collections';
      } else if (item.url.includes('/products/')) {
        category = 'Products';
      } else if (item.url.includes('/pages/')) {
        category = 'Pages';
      } else {
        category = 'Other';
      }
      if (!cats[category]) cats[category] = [];
      cats[category].push(item);
    });

    return Object.entries(cats)
      .sort((a, b) => {
        if (a[0] === 'Products') return -1;
        if (b[0] === 'Products') return 1;
        if (a[0] === 'Help') return -1;
        if (b[0] === 'Help') return 1;
        if (a[0] === 'Size Charts') return -1;
        if (b[0] === 'Size Charts') return 1;
        return b[1].length - a[1].length;
      })
      .map(([name, items]) => ({ name, items }));
  }

  function renderTabs(categories, activeCategory) {
    if (categories.length <= 1) return '';
    return `
      <div class="st-tabs">
        <button class="st-tab ${activeCategory === 'All' ? 'st-tab--active' : ''}" data-category="All">
          All
        </button>
        ${categories
          .map(
            (cat) => `
          <button
            class="st-tab ${cat.name === activeCategory ? 'st-tab--active' : ''}"
            data-category="${escapeHtml(cat.name)}">
            ${escapeHtml(cat.name)}
          </button>
        `
          )
          .join('')}
      </div>
    `;
  }

  function initSearch(root) {
    const ENGN = encodeURIComponent(root.dataset.engineKey || '');
    const input = root.querySelector('#st-search-input');
    const container = root.querySelector('#st-results-container');
    const suggestions = root.querySelector('#st-suggestions');
    const form = input ? input.form : null;
    const closeBtn = root.querySelector('#st-search-close');
    const isDrawer = root.closest('#search-drawer') !== null;

    if (!input || !container || !suggestions || !form) return;

    let EXTERNAL_URL = container.dataset.externalUrl || '';
    // If the external link points at the page we're already on, behave as a
    // local instance - the same section config can then power both the
    // redirecting widgets and the destination page without self-redirecting.
    if (EXTERNAL_URL) {
      try {
        const target = new URL(EXTERNAL_URL, window.location.origin);
        if (target.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '')) {
          EXTERNAL_URL = '';
        }
      } catch (e) {}
    }
    const DOMAIN_FILTER = getDomainFilter(root);
    // Server-side filter on the domain-identifier meta tag rendered in
    // theme.liquid, so other-domain results don't consume per_page slots.
    // Only effective once Swiftype has recrawled pages carrying the tag.
    const DOMAIN_FILTER_PARAM = DOMAIN_FILTER
      ? `&filters[page][domain-identifier]=${encodeURIComponent(DOMAIN_FILTER)}`
      : '';
    const filterMode = container.dataset.filterMode || 'some';
    const raw = (container.dataset.filter || '').trim();

    let FILTER_URLS = raw === '' ? [] : raw.split(',').map((f) => f.trim());

    // Help pages scope their Swift Search sections to help articles. The
    // drawer is sitewide search, so it keeps finding products everywhere.
    if (!isDrawer && /customer-cent(?:re|er)/.test(window.location.pathname)) {
      FILTER_URLS = HELP_URL_TERMS;
    }

    let currentPage = 1;
    let currentQuery = '';
    let allResults = [];
    let pageChrome = { prefix: '', suffix: '' };
    let categories = [];
    let currentCategory = 'All';
    let suggestTimer;
    // Bumped on every request (and on anything that supersedes one) so a late
    // response from an older request is ignored instead of overwriting newer UI.
    let suggestRequest = 0;
    let searchRequest = 0;

    // Highlight on the plain text, then escape, so terms like "amp" or "mark"
    // can't match inside entities or tags, and "c++" or "(" can't break the regex.
    function highlightQuery(query) {
      const terms = queryTerms(query);
      if (!terms.length) return;
      const regex = termRegex(terms);
      container.querySelectorAll('.st-result-link, .st-result-body').forEach((el) => {
        const parts = el.textContent.split(regex);
        if (parts.length < 2) return;
        // split() with a capture group puts the matches at the odd indexes.
        el.innerHTML = parts
          .map((part, i) => (i % 2 ? `<mark>${escapeHtml(part)}</mark>` : escapeHtml(part)))
          .join('');
      });
    }

    function renderPage(results, page) {
      const start = (page - 1) * PER_PAGE;
      const end = start + PER_PAGE;
      const pageResults = results.slice(start, end);
      const totalPages = Math.ceil(results.length / PER_PAGE);

      container.style.minHeight = container.offsetHeight + 'px';

      if (pageResults.length === 0) {
        container.innerHTML = renderTabs(categories, currentCategory) + '<p>No results found.</p>';
        return;
      }

      const cards = pageResults
        .map((item) => {
          const isProduct = item.url.includes('/products/');
          const isCollection = item.url.includes('/collections/');

          let bottomText = '';
          if (isProduct) {
            const price = getPrice(item.body || '');
            bottomText = price
              ? `<p class="st-result-price" style="margin:unset !important">${escapeHtml(price)}</p>`
              : '';
          } else if (!isCollection) {
            const snippet = getSnippet(item, queryTerms(currentQuery), pageChrome);
            bottomText = snippet
              ? `<p class="st-result-body" style="margin:unset !important; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;">${escapeHtml(snippet)}</p>`
              : '';
          }

          return `
        <div class="st-result">
          <a href="${escapeHtml(safeUrl(item.url))}" style="text-decoration: none !important; padding-bottom: 20px;">
            <div class="h-stack gap-4">
              <img src="${escapeHtml(safeUrl(item.image || ''))}" alt="${escapeHtml(
            item.title
          )}" style="width: 96px; height: 96px; object-fit: cover; border-radius: 2px; flex-shrink: 0; margin-bottom: 6px;" />
              <div class="v-stack" ${
                bottomText ? 'style="margin:unset;"' : 'style="margin:unset; align-self: baseline;"'
              }>
                <span class="st-result-link">${escapeHtml(item.title)}</span>
                ${bottomText}
              </div>
            </div>
          </a>
        </div>
      `;
        })
        .join('');

      const pagination =
        totalPages > 1
          ? `
      <div class="st-pagination">
        <button data-page="${page - 1}" ${page <= 1 ? 'style="visibility: hidden;"' : ''}>Previous</button>
        <span>Page ${page} of ${totalPages}</span>
        <button data-page="${page + 1}" ${page >= totalPages ? 'style="visibility: hidden;"' : ''}>Next</button>
      </div>
    `
          : '';

      container.innerHTML = renderTabs(categories, currentCategory) + cards + pagination;
    }

    function renderResults() {
      const filtered =
        currentCategory === 'All'
          ? allResults
          : categories.find((c) => c.name === currentCategory)?.items || allResults;
      renderPage(filtered, currentPage);
      highlightQuery(currentQuery);
    }

    function goToPage(page) {
      currentPage = page;
      renderResults();
      input.scrollIntoView({ behavior: 'smooth' });
    }

    function hideSuggestions() {
      suggestRequest++;
      suggestions.style.display = 'none';
      suggestions.innerHTML = '';
    }

    function showSuggestions(query) {
      const request = ++suggestRequest;
      fetch(
        `https://api.swiftype.com/api/v1/public/engines/suggest.json?engine_key=${ENGN}&q=${encodeURIComponent(
          query
        )}&per_page=5${DOMAIN_FILTER_PARAM}`
      )
        .then((r) => r.json())
        .then((data) => {
          if (request !== suggestRequest) return;
          const items = (data.records.page || []).filter((item) => {
            if (!matchesDomain(item.url, DOMAIN_FILTER)) return false;
            if (item.url.includes('/en-nz/')) return false;
            if (!HELP_URL.test(item.url) && !SUGGEST_ALLOWED.some((p) => item.url.includes(p))) return false;
            return FILTER_URLS.length === 0 || FILTER_URLS[filterMode]((f) => item.url.includes(f));
          });

          if (!items.length) {
            hideSuggestions();
            return;
          }

          const hasResults = container.querySelector('.st-result') !== null;
          suggestions.className = hasResults ? 'results-active' : '';

          suggestions.innerHTML = items
            .map(
              (item) => `
          <a href="${escapeHtml(safeUrl(item.url))}" class="st-suggestion">
            ${escapeHtml(item.title)}
          </a>
        `
            )
            .join('');

          suggestions.style.display = 'block';
        })
        .catch(() => {
          if (request === suggestRequest) hideSuggestions();
        });
    }

    function search(query) {
      if (!query) return;
      clearTimeout(suggestTimer);
      hideSuggestions();
      currentQuery = query;
      currentPage = 1;
      const request = ++searchRequest;

      container.innerHTML = `
      <div class="v-stack gap-4 sm:gap-6">
        ${Array.from(
          { length: 4 },
          () => `
          <div class="h-stack align-center gap-5 sm:gap-6">
            <span class="skeleton skeleton--thumbnail"></span>
            <div class="v-stack gap-3 w-full">
              <span class="skeleton skeleton--text" style="--skeleton-text-width: 20%"></span>
              <span class="skeleton skeleton--text" style="--skeleton-text-width: 80%"></span>
              <span class="skeleton skeleton--text" style="--skeleton-text-width: 40%"></span>
            </div>
          </div>
        `
        ).join('')}
      </div>
    `;

      fetch(
        `https://api.swiftype.com/api/v1/public/engines/search.json?engine_key=${ENGN}&q=${encodeURIComponent(
          query
        )}&per_page=100${DOMAIN_FILTER_PARAM}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (request !== searchRequest) return;
          const siteResults = (data.records.page || []).filter(
            (item) => matchesDomain(item.url, DOMAIN_FILTER) && !item.url.includes('/en-nz/')
          );
          pageChrome = getPageChrome(siteResults);
          allResults = siteResults.filter(
            (item) => FILTER_URLS.length === 0 || FILTER_URLS[filterMode]((f) => item.url.includes(f))
          );

          categories = categorizeResults(allResults);
          currentCategory = 'All';
          renderResults();
          if (closeBtn) closeBtn.style.top = '-1%';
        })
        .catch((err) => {
          if (request !== searchRequest) return;
          console.log(err);
          container.innerHTML = '<p>Something went wrong, please try again.</p>';
        });
    }

    function submitQuery(query) {
      if (EXTERNAL_URL) {
        const target = new URL(EXTERNAL_URL, window.location.href);
        target.searchParams.set('q', query);
        window.location.href = target.href;
        return;
      }
      search(query);
    }

    // The form posts to Shopify search if this script never runs; here it is
    // handled in place (Enter and the search button both submit it).
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      clearTimeout(suggestTimer);
      hideSuggestions();
      const query = input.value.trim();
      if (query) submitQuery(query);
    });

    input.addEventListener('input', function () {
      clearTimeout(suggestTimer);
      const query = input.value.trim();

      if (!query) {
        hideSuggestions();
        searchRequest++;
        container.innerHTML = '';
        container.style.minHeight = '0';
        if (closeBtn) closeBtn.style.top = '-16%';
        return;
      }

      suggestTimer = setTimeout(() => {
        showSuggestions(query);
      }, 400);
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) {
        hideSuggestions();
      }
    });

    container.addEventListener('click', function (e) {
      if (e.target.matches('.st-tab')) {
        currentCategory = e.target.dataset.category;
        currentPage = 1;
        renderResults();
      }
      if (e.target.matches('.st-pagination button')) {
        goToPage(parseInt(e.target.dataset.page, 10));
      }
    });

    // Run a query passed via ?q= (from an external-results instance) on load.
    // External-mode instances redirect instead of rendering, and the drawer
    // would only duplicate the page widget's fetch, so both are skipped.
    const initialQuery = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (initialQuery && !EXTERNAL_URL) {
      if (!isDrawer) {
        input.value = initialQuery;
        search(initialQuery);
      }
    }
  }

  function initAll() {
    document.querySelectorAll('.st-search').forEach((root) => {
      if (root.dataset.stInit) return;
      root.dataset.stInit = 'true';
      initSearch(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
